import { useEffect, useMemo } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { useSnapshot } from 'valtio';

import { getHomepageUrl } from '@cartwhl/common/utils';
import useMediaQuery from '@cartwhl/ui/hooks/useMediaQuery';
import ResponsiveSectionLayout from '@cartwhl/ui/layout/ResponsiveSectionLayout';

import { authStore } from 'stores/global';

import {
  findDashboardSection,
  getDashboardSections,
} from '../dashboardSections';
import { useStore } from '../dashboardStore';
import DashboardPlaceholderSection from './DashboardPlaceholderSection';

export default function Dashboard() {
  const { tab: sectionSlug } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const { authStatus } = useSnapshot(authStore.state);
  const dashboardStore = useStore();
  const { showAdmin, userStoreLoading } = dashboardStore.state;
  const loginUrl = useMemo(() => {
    const redirectTo = `apps${pathname}${search}`;
    return getHomepageUrl(
      `/login?redirectTo=${encodeURIComponent(redirectTo)}`,
    );
  }, [pathname, search]);

  useEffect(() => {
    if (authStatus === 'user') return;
    window.location.replace(loginUrl);
  }, [authStatus, loginUrl]);

  if (authStatus !== 'user') {
    return null;
  }

  const sections = getDashboardSections({ showAdmin });
  const activeSection = findDashboardSection(sections, sectionSlug);
  const sectionRedirectPath = isDesktop ? '/dashboard' : '/';
  const pageClassName = 'mx-6 mb-20 lg:container';

  if (sectionSlug === 'admin' && userStoreLoading && !showAdmin) {
    return (
      <main className="mt-8">
        <div className={pageClassName}>
          <DashboardPlaceholderSection
            title="Admin"
            description="Checking admin access…"
          />
        </div>
      </main>
    );
  }

  if (sectionSlug && !activeSection) {
    return <Navigate to={sectionRedirectPath} replace />;
  }

  return (
    <main className="mt-8">
      <ResponsiveSectionLayout
        sections={sections}
        activeSlug={activeSection?.slug}
        navigationLabel="Dashboard sections"
        className={pageClassName}
        desktopContentClassName="min-w-0 flex-auto"
        mobileContentClassName="mx-auto max-w-(--breakpoint-md)"
        onSelect={(nextSectionSlug) => navigate(`/${nextSectionSlug}`)}
        onMobileBack={() => navigate('/')}
      />
    </main>
  );
}
